import { onBeforeUnmount, ref, type Ref, watch } from 'vue'
import {
  type FilterAutocompleteSuggestion,
  getFilterAutocompleteSuggestions,
} from '@/api/filter-options'
import { priorities } from '@/utils/constants'

const FIELD_TYPE_CONFIG: Record<string, { label: string; filterProperty: string; icon: string }> = {
  Division: { label: 'Division', filterProperty: 'OwnerId', icon: 'hive' },
  User: {
    label: 'Requester',
    filterProperty: 'RequesterSid',
    icon: 'person_pin',
  },
  Requester: {
    label: 'Requester',
    filterProperty: 'RequesterSid',
    icon: 'person_pin',
  },
  Creator: {
    label: 'Creator',
    filterProperty: 'SubmitterSid',
    icon: 'record_voice_over',
  },
  FieldWorker: {
    label: 'Field Worker',
    filterProperty: 'FieldWorker',
    icon: 'engineering',
  },
  ResourceType: {
    label: 'Resource Type',
    filterProperty: 'ResourceTypeId',
    icon: 'local_shipping',
  },
  Activity: { label: 'Activity', filterProperty: 'TaskId', icon: 'build' },
  ServiceProvider: {
    label: 'Service Provider',
    filterProperty: 'ServiceProviderId',
    icon: 'villa',
  },
  Resource: {
    label: 'Resource',
    filterProperty: 'ResourceId',
    icon: 'navigation',
  },
  Location: {
    label: 'Location',
    filterProperty: 'LocationId',
    icon: 'explore',
  },
  CostCenter: {
    label: 'Cost Center',
    filterProperty: 'CostCenter',
    icon: 'payment',
  },
  Tag: { label: 'Tag', filterProperty: 'Tags', icon: 'label' },
  Priority: { label: 'Priority', filterProperty: 'Priority', icon: 'outlined_flag' },
}

export type AutocompleteSuggestion = FilterAutocompleteSuggestion & {
  filterProperty: string
  fieldLabel: string
  icon: string
}

export const AUTOCOMPLETE_RESULT_LIMIT = 12

function getMatchingPrioritySuggestions(query: string): AutocompleteSuggestion[] {
  if (!query || query.length < 3) return []

  const lowerQuery = query.toLowerCase()
  const config = FIELD_TYPE_CONFIG.Priority

  return priorities
    .filter((p) => p.name.toLowerCase().startsWith(lowerQuery))
    .map((p) => ({
      fieldType: 'Priority',
      valueId: String(p.id),
      displayValue: p.name,
      filterProperty: config.filterProperty,
      fieldLabel: config.label,
      icon: config.icon,
    }))
}

export function useFilterAutocomplete(
  searchText: Ref<string | null | undefined>,
  enabled: Ref<boolean>,
) {
  const suggestions = ref<AutocompleteSuggestion[]>([])
  const isLoading = ref(false)
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let abortController: AbortController | null = null

  const fetchSuggestions = async (query: string) => {
    if (abortController) {
      abortController.abort()
    }

    if (!query || query.length < 3) {
      suggestions.value = []
      isLoading.value = false
      return
    }

    abortController = new AbortController()
    isLoading.value = true

    try {
      const results = await getFilterAutocompleteSuggestions(
        query,
        AUTOCOMPLETE_RESULT_LIMIT,
        abortController.signal,
      )
      const priorityMatches = getMatchingPrioritySuggestions(query)
      suggestions.value = [
        ...priorityMatches,
        ...results.map((r) => {
          const config = FIELD_TYPE_CONFIG[r.fieldType]
          return {
            ...r,
            filterProperty: config?.filterProperty ?? r.fieldType,
            fieldLabel: config?.label ?? r.fieldType,
            icon: config?.icon ?? 'search',
          }
        }),
      ]
    } catch {
      // Cancelled or failed — ignore
    } finally {
      isLoading.value = false
    }
  }

  watch(searchText, (newValue) => {
    if (!enabled.value) return

    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    const trimmed = newValue?.trim() ?? ''
    debounceTimer = setTimeout(() => {
      fetchSuggestions(trimmed)
    }, 300)
  })

  const clear = () => {
    suggestions.value = []
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    if (abortController) {
      abortController.abort()
      abortController = null
    }
  }

  onBeforeUnmount(clear)

  return { suggestions, isLoading, clear }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function buildHighlightedHtml(
  text: string,
  ranges: Array<{
    start: number
    end: number
  }>,
) {
  let result = ''
  let currentIndex = 0

  for (const range of ranges) {
    result += escapeHtml(text.slice(currentIndex, range.start))
    result += `<strong>${escapeHtml(text.slice(range.start, range.end))}</strong>`
    currentIndex = range.end
  }

  result += escapeHtml(text.slice(currentIndex))

  return result
}

export function highlightMatches(text: string, query?: string | null): string {
  const sanitized = (query ?? '').replace(/[""+><()~*@]/g, ' ')
  const parts = sanitized.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return escapeHtml(text)

  const pattern = parts.map((p) => escapeRegex(p)).join(' ?[\\/-]? ?')
  const regex = new RegExp(pattern, 'i')
  const match = text.match(regex)

  if (!match) {
    const ranges: Array<{ start: number; end: number }> = []
    let searchStart = 0

    for (const part of parts) {
      const partRegex = new RegExp(escapeRegex(part), 'i')
      const partMatch = text.slice(searchStart).match(partRegex)

      if (!partMatch) {
        return escapeHtml(text)
      }

      const startIndex = searchStart + (partMatch.index ?? 0)
      const endIndex = startIndex + partMatch[0].length

      ranges.push({ start: startIndex, end: endIndex })
      searchStart = endIndex
    }

    return buildHighlightedHtml(text, ranges)
  }

  const startIndex = match.index ?? 0
  return buildHighlightedHtml(text, [{ start: startIndex, end: startIndex + match[0].length }])
}
